import 'react-app-polyfill/stable';
import './index.css';
import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { Routes } from 'Routes';
import { env } from 'constants/env';
import { FullStory } from 'V2/Analytics';
import { FeatureFlags } from 'V2/Utilities/FeatureFlags';
import { booleanStringRule } from 'V2/Utilities/FeatureFlags/rules';
import version from './version';

if (env.nodeEnv !== 'development') {
  Sentry.init({
    dsn: 'https://1bc82ffb33ae4027aca9c9e50bb452bf@o90268.ingest.sentry.io/5828859',
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.2,
    environment: `${env.clearcoverEnv}`,
    release: `acquisition-app@${version.git_sha}`,
    ignoreErrors: ['fp/tags', 'instantSearchSDKJSBridgeClearHighlight'],
  });
}

// Configure in Datadog console but customized:  see https://app.datadoghq.com/rum/application/6d49ee3a-3417-4f69-8fc0-0e4948cc7700/sdk
const datadogConfig = {
  clientToken: env.dataDogClientToken,
  site: 'datadoghq.com',
  env: env.clearcoverEnv,
  service: 'acquisition-app',
  version: env.nodeEnv === 'development' ? 'development' : version.git_sha,
};

datadogRum.init({
  applicationId: env.dataDogId,
  ...datadogConfig,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 0,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'mask-user-input',
  allowedTracingUrls: [env.apiHost],
});

datadogLogs.init({
  forwardErrorsToLogs: true,
  ...datadogConfig,
});

FullStory.init({ enabled: env.fullstoryEnabled === '1' });

const Application = () => {
  useEffect(() => {
    FeatureFlags.init({
      payment_flexibility_downpayment_slider:
        env.paymentFlexibilityDownpaymentSlider,
      payment_flexibility_every_two_weeks: env.paymentFlexibilityEveryTwoWeeks,
      coverages_component_bind_API: env.coveragesComponentBindAPI,
      monetization_widget: env.monetizationWidget,
      nextinsure_widget: env.nextinsureWidget,
      display_quote_magic_progress_bar: env.displayQuoteMagicProgressBar,
      trustedform_enabled: env.trustedformEnabled,
      split_vehicle_year_make_model: env.splitVehicleYearMakeModel,
      consent_capture_enabled: env.consentCaptureEnabled,
    });

    FeatureFlags.setRules({
      payment_flexibility: booleanStringRule,
      payment_flexibility_downpayment_slider: booleanStringRule,
      payment_flexibility_every_two_weeks: booleanStringRule,
      coverages_component_bind_API: booleanStringRule,
      monetization_widget: booleanStringRule,
      nextinsure_widget: booleanStringRule,
      display_quote_magic_progress_bar: booleanStringRule,
      trustedform_enabled: booleanStringRule,
      split_vehicle_year_make_model: booleanStringRule,
      consent_capture_enabled: booleanStringRule,
    });
  }, []);

  return (
    <Router>
      <Routes />
    </Router>
  );
};

// Vite fires this event when a preloaded chunk 404s after a new deployment.
// Auto-reload so users don't see a broken step (replaces the Webpack-specific
// ChunkLoadError handling that DynamicStepLoader.tsx previously relied on).
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  window.location.reload();
});

const root = createRoot(document.getElementById('root')!);
root.render(<Application />);
